package com.iptvplayer.shared.data.parser

import com.iptvplayer.shared.domain.model.Channel
import com.iptvplayer.shared.domain.model.IptvSource
import com.iptvplayer.shared.domain.model.Playlist
import com.iptvplayer.shared.domain.model.StreamType
import com.iptvplayer.shared.domain.model.VodItem

class M3UParser {

    fun parse(content: String, source: IptvSource): Playlist {
        val lines = content
            .replace("\r\n", "\n")
            .replace("\r", "\n")
            .split("\n")

        val channels = mutableListOf<Channel>()
        val vodItems = mutableListOf<VodItem>()
        val categories = mutableSetOf<String>()

        // URL-hash ids with a suffix for duplicate entries (some playlists
        // repeat the same URL) so ids stay unique within the playlist.
        val usedIds = HashSet<String>()
        fun uniqueId(prefix: String, url: String): String {
            val base = "${prefix}_${stableId(url)}"
            var id = base
            var n = 2
            while (!usedIds.add(id)) {
                id = "${base}_$n"
                n++
            }
            return id
        }

        var i = 0
        // Skip optional #EXTM3U header
        if (lines.isNotEmpty() && lines[0].trim().startsWith("#EXTM3U")) {
            i = 1
        }

        while (i < lines.size) {
            val line = lines[i].trim()

            if (line.startsWith("#EXTINF:")) {
                // Parse the #EXTINF line
                val extinf = parseExtInfLine(line)

                // Find the next non-empty, non-comment line as the URL
                i++
                var url: String? = null
                while (i < lines.size) {
                    val nextLine = lines[i].trim()
                    if (nextLine.isNotEmpty() && !nextLine.startsWith("#")) {
                        url = nextLine
                        break
                    }
                    // Skip #EXTVLCOPT and other directives
                    i++
                }

                if (url != null) {
                    val group = extinf.groupTitle
                    if (group != null) {
                        categories.add(group)
                    }

                    val streamType = detectStreamType(url)
                    val isVod = isVodContent(url, extinf)

                    if (isVod) {
                        vodItems.add(
                            VodItem(
                                id = uniqueId("${source.id}_vod", url),
                                sourceId = source.id,
                                name = extinf.title,
                                streamUrl = url,
                                coverUrl = extinf.tvgLogo,
                                plot = null,
                                genre = group,
                                duration = if (extinf.duration > 0) extinf.duration.toLong() * 1000L else null,
                                rating = null,
                                group = group
                            )
                        )
                    } else {
                        channels.add(
                            Channel(
                                id = uniqueId("${source.id}_ch", url),
                                sourceId = source.id,
                                name = extinf.title,
                                streamUrl = url,
                                logoUrl = extinf.tvgLogo,
                                group = group,
                                streamType = streamType
                            )
                        )
                    }
                }
            }
            i++
        }

        return Playlist(
            source = source,
            channels = channels,
            vodItems = vodItems,
            categories = categories.toList().sorted()
        )
    }

    /**
     * Stable id derived from the stream URL (FNV-1a, hex). Positional ids
     * (`_vod_${index}`) silently broke favorites, history, and resume whenever
     * the provider reordered or inserted playlist entries.
     */
    private fun stableId(url: String): String {
        var hash = -0x340d631b7bdddcdbL // FNV-1a 64-bit offset basis
        for (ch in url) {
            hash = hash xor ch.code.toLong()
            hash *= 0x100000001b3L
        }
        return hash.toULong().toString(16)
    }

    private fun parseExtInfLine(line: String): ExtInfData {
        // Format: #EXTINF:<duration> [key="value" ...],<title>
        val afterPrefix = line.removePrefix("#EXTINF:")

        // Extract duration (the number before the first space or comma)
        val durationEnd = afterPrefix.indexOfFirst { it == ' ' || it == ',' }
        val durationStr = if (durationEnd >= 0) afterPrefix.substring(0, durationEnd) else afterPrefix
        val duration = durationStr.toIntOrNull() ?: -1

        // Extract attributes and title
        val commaIndex = findTitleComma(afterPrefix)
        val title: String
        val attributesPart: String

        if (commaIndex >= 0) {
            title = afterPrefix.substring(commaIndex + 1).trim()
            attributesPart = afterPrefix.substring(0, commaIndex)
        } else {
            title = afterPrefix.trim()
            attributesPart = ""
        }

        val tvgId = extractAttribute(attributesPart, "tvg-id")
        val tvgName = extractAttribute(attributesPart, "tvg-name")
        val tvgLogo = extractAttribute(attributesPart, "tvg-logo")
        val groupTitle = extractAttribute(attributesPart, "group-title")

        return ExtInfData(
            duration = duration,
            tvgId = tvgId,
            tvgName = tvgName,
            tvgLogo = tvgLogo,
            groupTitle = groupTitle,
            title = title.ifEmpty { tvgName ?: "Unknown" }
        )
    }

    /**
     * Find the comma that separates attributes from the channel title.
     * This must handle commas inside quoted attribute values.
     */
    private fun findTitleComma(str: String): Int {
        var inQuotes = false
        var lastCommaIndex = -1
        for (i in str.indices) {
            when (str[i]) {
                '"' -> inQuotes = !inQuotes
                ',' -> if (!inQuotes) lastCommaIndex = i
            }
        }
        return lastCommaIndex
    }

    private fun extractAttribute(attributes: String, key: String): String? {
        // Match key="value" or key='value'
        val patterns = listOf(
            Regex("""$key="([^"]*?)""""),
            Regex("""$key='([^']*?)'""")
        )
        for (pattern in patterns) {
            val match = pattern.find(attributes)
            if (match != null) {
                val value = match.groupValues[1].trim()
                return value.ifEmpty { null }
            }
        }
        return null
    }

    private fun detectStreamType(url: String): StreamType {
        val lower = url.lowercase()
        return when {
            lower.contains(".m3u8") || lower.contains("/hls/") -> StreamType.HLS
            lower.endsWith(".ts") || lower.contains("/live/") -> StreamType.MPEGTS
            lower.contains(".mpd") || lower.contains("/dash/") -> StreamType.DASH
            else -> StreamType.HLS // Default to HLS as most common
        }
    }

    private fun isVodContent(url: String, extinf: ExtInfData): Boolean {
        val lower = url.lowercase()
        val isMovieExtension = lower.endsWith(".mp4") ||
                lower.endsWith(".mkv") ||
                lower.endsWith(".avi") ||
                lower.endsWith(".mov") ||
                lower.endsWith(".wmv") ||
                lower.endsWith(".flv")
        val isMoviePath = lower.contains("/movie/") || lower.contains("/movies/")
        val isLongDuration = extinf.duration > 300 // More than 5 minutes suggests VOD
        val groupSuggestsVod = extinf.groupTitle?.lowercase()?.let {
            it.contains("vod") || it.contains("movie") || it.contains("film") || it.contains("series")
        } ?: false

        return isMovieExtension || isMoviePath || (isLongDuration && groupSuggestsVod)
    }

    private data class ExtInfData(
        val duration: Int,
        val tvgId: String?,
        val tvgName: String?,
        val tvgLogo: String?,
        val groupTitle: String?,
        val title: String
    )
}
